'use client'

import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Loader2, RefreshCw } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import Image from 'next/image'
import { useGetPokemons } from './hooks/use-pokemon-queries'
import { usePrefetchPokemon } from './hooks/use-pokemon-cache'
import { useGetPokemonDetail } from './hooks/use-pokemon-queries'
import { useInvalidatePokemon } from './hooks/use-pokemon-cache'


export default function QueryDemo() {
  const [page, setPage] = useState(1)
  const [selectedPokemon, setSelectedPokemon] = useState('')
  const { data, isLoading, isError, error, refetch, hasNextPage, hasPreviousPage } = useGetPokemons(page)
  const { data: pokemonDetail, isLoading: isLoadingDetail } = useGetPokemonDetail(selectedPokemon)
  const { invalidateAll } = useInvalidatePokemon()
  const { prefetchNextPage } = usePrefetchPokemon()

  if (isError) {
    return (
      <div className="container mx-auto py-10">
        <Card className="bg-destructive/10">
          <CardHeader>
            <CardTitle className="text-destructive">Error</CardTitle>
            <CardDescription>{(error as Error).message}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-10 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold">React Query Demo</h1>
        <div className="space-x-2">
          <Button onClick={() => invalidateAll()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Invalidate Cache
          </Button>
          <Button onClick={() => refetch()}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              'Refresh Data'
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Pokemon List</CardTitle>
            <CardDescription>
              Page {page} - This data is automatically cached and revalidated by React Query
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : (
              <div className="grid gap-2">
                {data?.results.map((pokemon) => (
                  <Card 
                    key={pokemon.name}
                    className={`cursor-pointer transition-colors ${
                      selectedPokemon === pokemon.name ? 'border-primary' : ''
                    }`}
                    onClick={() => setSelectedPokemon(pokemon.name)}
                  >
                    <CardHeader>
                      <CardTitle className="capitalize">{pokemon.name}</CardTitle>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => setPage(p => p - 1)}
              disabled={!hasPreviousPage}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setPage(p => p + 1)
                prefetchNextPage(page)
              }}
              disabled={!hasNextPage}
            >
              Next
            </Button>
          </CardFooter>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Pokemon Details</CardTitle>
            <CardDescription>
              Click on a Pokemon to see its details (demonstrates dependent queries)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!selectedPokemon ? (
              <div className="text-center text-muted-foreground">
                Select a Pokemon to view details
              </div>
            ) : isLoadingDetail ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : pokemonDetail ? (
              <div className="space-y-4">
                <div className="flex justify-center">
                  {pokemonDetail.sprites.front_default && (
                    <Image
                      src={pokemonDetail.sprites.front_default}
                      alt={pokemonDetail.name}
                      width={150}
                      height={150}
                    />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Stats</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div>Height: {pokemonDetail.height / 10}m</div>
                    <div>Weight: {pokemonDetail.weight / 10}kg</div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Types</h3>
                  <div className="flex gap-2">
                    {pokemonDetail.types.map(({ type }) => (
                      <Badge key={type.name} variant="secondary">
                        {type.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Advanced Features Demonstrated</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc list-inside space-y-2">
            <li>Pagination with automatic prefetching of next page</li>
            <li>Cache invalidation (try the Invalidate Cache button)</li>
            <li>Dependent queries (Pokemon details load when selected)</li>
            <li>Parallel queries (list and detail load independently)</li>
            <li>Optimistic updates and cache management</li>
            <li>Type-safe data with TypeScript</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  )
} 
